/**
 * Le formulaire de création d'une facture — qui crée un BROUILLON, pas une facture.
 *
 * Deux changements du lot H se voient ici :
 *
 * **La liste des agrégateurs vient du serveur** (item H4). Symphonics et Survoltage
 * n'apparaissent plus en dur : `/factures/agregateurs` sert le catalogue, donc un
 * agrégateur ajouté par le lot F devient facturable sans qu'on retouche cet écran.
 *
 * **Le calcul automatique n'est proposé que là où il existe.** Un agrégateur ajouté n'a
 * aucun barème derrière lui ; proposer « Automatique » produirait une facture vide qu'on
 * croirait calculée. L'option est donc désactivée et le dit.
 */
import { useEffect, useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import { FileText, Plus, Trash2, Wand2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Modale from './Modale';

const LIGNE_VIDE = { description: '', puissance_kw: '', revenu_estime_ht: '' };
const premierJourDuMois = () => new Date(new Date().setDate(1)).toISOString().slice(0, 10);
const aujourdhui = () => new Date().toISOString().slice(0, 10);

export default function NouvelleFacture({ onClose, onCreee }) {
  const [agregateurs, setAgregateurs] = useState([]);
  const [form, setForm] = useState({
    agregateur: '',
    periode_debut: premierJourDuMois(),
    periode_fin: aujourdhui(),
    date_emission: aujourdhui(),
    taux_tva: 20,
    notes: '',
  });
  const [mode, setMode] = useState('auto');
  const [lignes, setLignes] = useState([{ ...LIGNE_VIDE }]);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/factures/agregateurs');
        setAgregateurs(r.data || []);
        if (r.data?.length) setForm((f) => ({ ...f, agregateur: r.data[0].cle }));
      } catch {
        toast.error('Impossible de charger la liste des agrégateurs');
      }
    })();
  }, []);

  const choisi = agregateurs.find((a) => a.cle === form.agregateur);
  const calculPossible = !!choisi?.calcul_automatique;
  // Basculer sur un agrégateur sans barème doit ramener la saisie manuelle, sinon le
  // formulaire resterait sur un mode que le serveur refuserait à l'envoi.
  const modeEffectif = calculPossible ? mode : 'manuel';

  const majLigne = (i, champ, valeur) =>
    setLignes(lignes.map((l, idx) => (idx === i ? { ...l, [champ]: valeur } : l)));

  const envoyer = async () => {
    setEnregistrement(true);
    try {
      const r = await api.post('/factures', {
        ...form,
        taux_tva: Number(form.taux_tva) || 20,
        auto_generate: modeEffectif === 'auto',
        lignes:
          modeEffectif === 'auto'
            ? []
            : lignes
                .filter((l) => l.description && l.revenu_estime_ht)
                .map((l) => ({
                  description: l.description,
                  puissance_kw: l.puissance_kw ? parseFloat(l.puissance_kw) : null,
                  revenu_estime_ht: parseFloat(l.revenu_estime_ht) || 0,
                })),
      });
      const n = r.data.lignes.length;
      toast.success(`Brouillon créé (${n} ligne${n > 1 ? 's' : ''}, TTC ${r.data.montant_ttc} €)`);
      onCreee(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setEnregistrement(false);
  };

  return (
    <Modale
      titre="Nouvelle facture"
      icone={FileText}
      onClose={onClose}
      pied={
        <>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={envoyer} disabled={enregistrement || !form.agregateur} data-testid="facture-submit">
            {enregistrement ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {enregistrement ? 'Création…' : 'Créer le brouillon'}
          </Button>
        </>
      }
    >
      <p className="text-[12.5px] text-neutral-500">
        La création produit un <strong>brouillon</strong> : rien n&apos;est engagé, aucun numéro
        n&apos;est consommé. Vous pourrez le relire en aperçu avant de le valider.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px]">Agrégateur *</Label>
          <Select value={form.agregateur} onValueChange={(v) => setForm({ ...form, agregateur: v })}>
            <SelectTrigger className="h-9" data-testid="facture-agregateur"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>
              {agregateurs.map((a) => (
                <SelectItem key={a.cle} value={a.cle}>{a.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Date d&apos;émission</Label>
          <Input type="date" value={form.date_emission} className="h-9" data-testid="facture-date-emission"
                 onChange={(e) => setForm({ ...form, date_emission: e.target.value })} />
        </div>
        <div>
          <Label className="text-[11px]">Période début *</Label>
          <Input type="date" value={form.periode_debut} className="h-9" data-testid="facture-periode-debut"
                 onChange={(e) => setForm({ ...form, periode_debut: e.target.value })} />
        </div>
        <div>
          <Label className="text-[11px]">Période fin *</Label>
          <Input type="date" value={form.periode_fin} className="h-9" data-testid="facture-periode-fin"
                 onChange={(e) => setForm({ ...form, periode_fin: e.target.value })} />
        </div>
        <div>
          <Label className="text-[11px]">Taux de TVA (%)</Label>
          <Input type="number" step="0.1" value={form.taux_tva} className="h-9" data-testid="facture-tva"
                 onChange={(e) => setForm({ ...form, taux_tva: e.target.value })} />
        </div>
      </div>

      <div className="p-3 rounded-md bg-neutral-50 border border-neutral-100">
        <p className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold mb-2">
          Lignes de la facture
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => calculPossible && setMode('auto')}
            disabled={!calculPossible}
            className={`flex-1 p-2 rounded border text-left text-[12px] ${
              modeEffectif === 'auto' ? 'border-emerald-500 bg-emerald-50' : 'border-neutral-200 bg-white'
            } ${calculPossible ? '' : 'opacity-50 cursor-not-allowed'}`}
            data-testid="mode-auto"
          >
            <p className="font-medium flex items-center gap-1"><Wand2 className="w-3.5 h-3.5" /> Automatique</p>
            <p className="text-[10.5px] text-neutral-500">
              {calculPossible
                ? 'Calcul depuis les montants réels, au prorata de la période'
                : `Aucun barème pour ${choisi?.nom || 'cet agrégateur'} : saisie manuelle`}
            </p>
          </button>
          <button
            onClick={() => setMode('manuel')}
            className={`flex-1 p-2 rounded border text-left text-[12px] ${
              modeEffectif === 'manuel' ? 'border-emerald-500 bg-emerald-50' : 'border-neutral-200 bg-white'
            }`}
            data-testid="mode-manuel"
          >
            <p className="font-medium">Saisie manuelle</p>
            <p className="text-[10.5px] text-neutral-500">Une ligne par actif ou par poste</p>
          </button>
        </div>
      </div>

      {modeEffectif === 'manuel' && (
        <div className="space-y-2" data-testid="lignes-manuelles">
          {lignes.map((l, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`ligne-row-${i}`}>
              <Input value={l.description} placeholder="Description" className="flex-1 h-9 text-[12px]"
                     data-testid={`ligne-desc-${i}`} onChange={(e) => majLigne(i, 'description', e.target.value)} />
              <Input value={l.puissance_kw} placeholder="kW" type="number" step="0.1" className="w-20 h-9 text-[12px]"
                     data-testid={`ligne-pw-${i}`} onChange={(e) => majLigne(i, 'puissance_kw', e.target.value)} />
              <Input value={l.revenu_estime_ht} placeholder="€ HT" type="number" step="0.01" className="w-24 h-9 text-[12px]"
                     data-testid={`ligne-eur-${i}`} onChange={(e) => majLigne(i, 'revenu_estime_ht', e.target.value)} />
              {lignes.length > 1 && (
                <button onClick={() => setLignes(lignes.filter((_, idx) => idx !== i))}
                        className="p-1 text-red-500 hover:bg-red-50 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setLignes([...lignes, { ...LIGNE_VIDE }])} data-testid="add-ligne-btn">
            <Plus className="w-3 h-3 mr-1" /> Ligne
          </Button>
        </div>
      )}

      <div>
        <Label className="text-[11px]">Observations</Label>
        <Textarea value={form.notes} rows={2} data-testid="facture-notes"
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </div>
    </Modale>
  );
}
